import React, { useEffect, useMemo, useRef, useState } from "react";
import logoFull from "./assets/ai_logo.png";
import { api, getToken, setToken } from "./lib/api";

type AuthUser = { id: string; username: string; isAdmin: boolean };
type Idea = {
  id: string;
  title: string;
  summary: string;
  tags: string[];
  author: string;
  createdAt: string;
  updatedAt: string;
  voteCount: number;
  commentCount: number;
};
type Comment = { id: string; ideaId: string; body: string; author: string; createdAt: string };
type Invite = { id: string; code: string; createdAt: string; usedAt?: string | null; usedById?: string | null };
type AdminUser = { id: string; username: string; isAdmin: boolean; isActive: boolean; createdAt: string };

type CommentState = {
  loading: boolean;
  items: Comment[];
  error: string | null;
};

const theme = {
  bg: "#0B1B2B",
  gold: "#C8A46B",
  text: "#F5F7FA",
  gray: "#9AA5B1",
  card: "#102235",
  cardBorder: "#1C3147",
  input: "#0E2236",
};

async function safeCopy(text: string) {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {}
  try {
    if (typeof document !== "undefined") {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand && document.execCommand("copy");
      document.body.removeChild(ta);
      return !!ok;
    }
  } catch {}
  return false;
}

function Shell({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.body.style.background = theme.bg;
      document.body.style.color = theme.text;
      document.body.style.fontFamily =
        "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial";
    }
  }, []);
  return (
    <div className="min-h-screen" style={{ background: theme.bg, color: theme.text }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</div>
    </div>
  );
}

function BrandHeader() {
  return (
    <header className="mb-8">
      <div className="max-w-4xl">
        <img src={logoFull} alt="The AI Capital Society — Ideas. Growth. Value." className="w-full h-auto" />
      </div>
    </header>
  );
}

function LogoMark({ size = 48 }: { size?: number }) {
  const borderWidth = Math.max(2, Math.round(size * 0.08));
  const diamondSize = size * 0.45;

  return (
    <div className="grid place-items-center rounded-full" style={{ width: size, height: size, border: `${borderWidth}px solid ${theme.gold}` }}>
      <div
        style={{
          width: diamondSize,
          height: diamondSize,
          background: theme.gold,
          transform: "rotate(45deg)",
        }}
      />
    </div>
  );
}

function Gate({ onSuccess }: { onSuccess: (user: AuthUser) => void }) {
  const inviteFromUrl = typeof window !== "undefined"
    ? (new URLSearchParams(window.location.search).get("invite") || "")
    : "";
  const [mode, setMode] = useState<"login" | "register">("login");
  const [step, setStep] = useState(1);

  const [code, setCode] = useState(inviteFromUrl.toUpperCase());
  const [q1, setQ1] = useState("");
  const [q2, setQ2] = useState("");
  const [q3, setQ3] = useState("");
  const [username, setUsername] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");

  const [loginU, setLoginU] = useState("");
  const [loginP, setLoginP] = useState("");

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const quizValid = q1 === "B" && q2 === "B" && q3 === "B";

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) {
      setError("Bitte Einladungscode eingeben");
      return;
    }
    setLoading(true);
    try {
      const result = await api.verifyInvite(trimmed);
      if (!result.valid) {
        setError("Ungültiger oder verbrauchter Code");
        return;
      }
      setCode(result.code);
      setStep(2);
    } catch (err) {
      setError((err as Error).message || "Validierung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const handleQuiz = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!quizValid) {
      setError("Prüfungsfragen falsch. Zugang verwehrt.");
      return;
    }
    setStep(3);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!code) return setError("Kein gültiger Code verwendet");
    if (!username.trim()) return setError("Bitte Benutzernamen eingeben");
    if (pw.length < 8) return setError("Passwort zu kurz");
    if (pw !== pw2) return setError("Passwörter stimmen nicht überein");

    setLoading(true);
    try {
      const result = await api.register({ username: username.trim(), password: pw, inviteCode: code });
      setToken(result.token);
      setMessage(result.user.isAdmin ? "Willkommen, Admin." : "Willkommen im Circle.");
      onSuccess(result.user);
    } catch (err) {
      setError((err as Error).message || "Registrierung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!loginU.trim() || !loginP) {
      setError("Bitte Zugangsdaten eingeben");
      return;
    }
    setLoading(true);
    try {
      const result = await api.login({ username: loginU.trim(), password: loginP });
      setToken(result.token);
      setMessage(result.user.isAdmin ? "Admin erfolgreich angemeldet." : "Erfolgreich angemeldet.");
      onSuccess(result.user);
    } catch (err) {
      setError((err as Error).message || "Anmeldung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl p-6 sm:p-8" style={{ background: theme.card, border: `1px solid ${theme.cardBorder}` }}>
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => {
            setMode("login");
            setError("");
            setMessage("");
          }}
          className={`px-3 py-1 rounded-full ${mode === "login" ? "font-semibold" : ""}`}
          style={{ border: `1px solid ${theme.cardBorder}`, background: mode === "login" ? theme.input : "transparent" }}
        >
          Anmelden
        </button>
        <button
          onClick={() => {
            setMode("register");
            setStep(1);
            setError("");
            setMessage("");
          }}
          className={`px-3 py-1 rounded-full ${mode === "register" ? "font-semibold" : ""}`}
          style={{ border: `1px solid ${theme.cardBorder}`, background: mode === "register" ? theme.input : "transparent" }}
        >
          Registrieren
        </button>
      </div>

      {mode === "login" && (
        <form onSubmit={handleLogin} className="grid gap-4">
          <h2 className="text-lg font-semibold">Anmeldung</h2>
          <input
            value={loginU}
            onChange={(e) => setLoginU(e.target.value)}
            placeholder="Benutzername"
            className="rounded-xl px-4 py-3 outline-none"
            style={{ background: theme.input, border: `1px solid ${theme.cardBorder}`, color: theme.text }}
          />
          <input
            type="password"
            value={loginP}
            onChange={(e) => setLoginP(e.target.value)}
            placeholder="Passwort"
            className="rounded-xl px-4 py-3 outline-none"
            style={{ background: theme.input, border: `1px solid ${theme.cardBorder}`, color: theme.text }}
          />
          <button
            type="submit"
            className="rounded-xl px-4 py-2"
            style={{ background: theme.gold, color: "#0C1117", opacity: loading ? 0.7 : 1 }}
            disabled={loading}
          >
            Anmelden
          </button>
        </form>
      )}

      {mode === "register" && (
        <>
          {step === 1 && (
            <form onSubmit={handleVerifyCode} className="grid gap-4">
              <h2 className="text-lg font-semibold">Einladungscode</h2>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.trim().toUpperCase())}
                placeholder="AICS-XXXX"
                className="rounded-xl px-4 py-3 outline-none"
                style={{ background: theme.input, border: `1px solid ${theme.cardBorder}`, color: theme.text }}
              />
              <button
                type="submit"
                className="rounded-xl px-4 py-2"
                style={{ background: theme.gold, color: "#0C1117", opacity: loading ? 0.7 : 1 }}
                disabled={loading}
              >
                Weiter
              </button>
            </form>
          )}
          {step === 2 && (
            <form onSubmit={handleQuiz} className="grid gap-5">
              <h2 className="text-lg font-semibold">Initiationsfragen</h2>
              <QuizBlock
                q={1}
                title="Ideas."
                value={q1}
                setValue={setQ1}
                options={[
                  { k: "A", t: "Ihr Schutz durch Geheimhaltung" },
                  { k: "B", t: "Ihre Umsetzung in die Realität" },
                  { k: "C", t: "Ihre Einzigartigkeit an sich" },
                ]}
              />
              <QuizBlock
                q={2}
                title="Growth."
                value={q2}
                setValue={setQ2}
                options={[
                  { k: "A", t: "Zufall und Glück" },
                  { k: "B", t: "Kontinuierliche Verbesserung und Skalierung" },
                  { k: "C", t: "Reines Kapital ohne Strategie" },
                ]}
              />
              <QuizBlock
                q={3}
                title="Capital."
                value={q3}
                setValue={setQ3}
                options={[
                  { k: "A", t: "Geld allein" },
                  { k: "B", t: "Wissen, Netzwerk und Ressourcen in Einheit" },
                  { k: "C", t: "Macht über andere" },
                ]}
              />
              <button
                type="submit"
                className="rounded-xl px-4 py-2"
                style={{ background: theme.gold, color: "#0C1117" }}
              >
                Weiter
              </button>
            </form>
          )}
          {step === 3 && (
            <form onSubmit={handleRegister} className="grid gap-4">
              <h2 className="text-lg font-semibold">Zugangsdaten</h2>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Benutzername"
                className="rounded-xl px-4 py-3 outline-none"
                style={{ background: theme.input, border: `1px solid ${theme.cardBorder}`, color: theme.text }}
              />
              <input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="Passwort (min 8 Zeichen)"
                className="rounded-xl px-4 py-3 outline-none"
                style={{ background: theme.input, border: `1px solid ${theme.cardBorder}`, color: theme.text }}
              />
              <input
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                placeholder="Passwort wiederholen"
                className="rounded-xl px-4 py-3 outline-none"
                style={{ background: theme.input, border: `1px solid ${theme.cardBorder}`, color: theme.text }}
              />
              <button
                type="submit"
                className="rounded-xl px-4 py-2"
                style={{ background: theme.gold, color: "#0C1117", opacity: loading ? 0.7 : 1 }}
                disabled={loading}
              >
                Registrieren
              </button>
            </form>
          )}
        </>
      )}

      {error && (
        <p className="mt-4 text-sm" style={{ color: "#FCA5A5" }}>
          {error}
        </p>
      )}
      {message && (
        <p className="mt-4 text-sm" style={{ color: theme.gray }}>
          {message}
        </p>
      )}
    </div>
  );
}

type QuizOption = { k: string; t: string };

function QuizBlock({
  q,
  title,
  value,
  setValue,
  options,
}: {
  q: number;
  title: string;
  value: string;
  setValue: (value: string) => void;
  options: QuizOption[];
}) {
  return (
    <div className="rounded-xl p-4" style={{ background: theme.input, border: `1px solid ${theme.cardBorder}` }}>
      <div className="font-medium mb-2">
        Frage {q} - {title}
      </div>
      <div className="grid gap-2">
        {options.map((opt) => (
          <label key={opt.k} className="flex items-center gap-3 cursor-pointer">
            <input type="radio" name={`q${q}`} checked={value === opt.k} onChange={() => setValue(opt.k)} />
            <span>{opt.t}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function AdminPanel({
  currentUser,
  invites,
  users,
  onCreateInvite,
  onDeleteInvite,
  onToggleActive,
  onToggleAdmin,
  loading,
}: {
  currentUser: AuthUser;
  invites: Invite[];
  users: AdminUser[];
  onCreateInvite: () => Promise<void>;
  onDeleteInvite: (code: string) => Promise<void>;
  onToggleActive: (id: string) => Promise<void>;
  onToggleAdmin: (id: string) => Promise<void>;
  loading: boolean;
}) {
  if (!currentUser.isAdmin) return null;

  const handleCopy = async (code: string) => {
    const ok = await safeCopy(code);
    if (!ok) {
      try {
        window.prompt("Zum Kopieren markieren und Strg/Cmd+C drücken:", code);
      } catch {}
    }
  };

  return (
    <div className="rounded-2xl p-6 mb-6" style={{ background: theme.card, border: `1px solid ${theme.cardBorder}` }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Admin • Mitglieder & Einladungen</h3>
        <span className="text-xs" style={{ color: theme.gray }}>
          Nur sichtbar für Admins
        </span>
      </div>

      <div className="grid gap-6">
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm" style={{ color: theme.gray }}>
              Einladungscodes
            </div>
            <button
              onClick={onCreateInvite}
              className="rounded-xl px-3 py-2 text-sm font-medium"
              style={{ background: theme.gold, color: "#0C1117", opacity: loading ? 0.7 : 1 }}
              disabled={loading}
            >
              + Code generieren
            </button>
          </div>
          {invites.length === 0 ? (
            <div
              className="rounded-xl p-4 text-sm"
              style={{ background: theme.input, border: `1px solid ${theme.cardBorder}`, color: theme.gray }}
            >
              Keine aktiven Einladungen.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {invites.map((inv) => (
                <div
                  key={inv.code}
                  className="flex items-center justify-between rounded-xl px-3 py-2"
                  style={{ background: theme.input, border: `1px solid ${theme.cardBorder}` }}
                >
                  <span className="font-mono text-sm">{inv.code}</span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleCopy(inv.code)} className="text-xs underline" style={{ color: theme.gray }}>
                      Kopieren
                    </button>
                    <button
                      onClick={() => onDeleteInvite(inv.code)}
                      className="text-xs underline"
                      style={{ color: theme.gray, opacity: loading ? 0.6 : 1 }}
                      disabled={loading}
                    >
                      Entziehen
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="text-sm mb-2" style={{ color: theme.gray }}>
            Mitglieder
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ color: theme.gray }}>
                  <th className="text-left py-2">Benutzername</th>
                  <th className="text-left py-2">Join</th>
                  <th className="text-left py-2">Rolle</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-left py-2">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t" style={{ borderColor: theme.cardBorder }}>
                    <td className="py-2">{u.username}</td>
                    <td className="py-2" style={{ color: theme.gray }}>
                      {new Date(u.createdAt).toLocaleString()}
                    </td>
                    <td className="py-2">{u.isAdmin ? "Admin" : "Member"}</td>
                    <td className="py-2">{u.isActive ? "Aktiv" : "Gesperrt"}</td>
                    <td className="py-2">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onToggleAdmin(u.id)}
                          className="rounded-lg px-2 py-1"
                          style={{ border: `1px solid ${theme.cardBorder}`, opacity: loading ? 0.6 : 1 }}
                          disabled={loading}
                        >
                          {u.isAdmin ? "Zu Mitglied" : "Zu Admin"}
                        </button>
                        <button
                          onClick={() => onToggleActive(u.id)}
                          className="rounded-lg px-2 py-1"
                          style={{ border: `1px solid ${theme.cardBorder}`, opacity: loading ? 0.6 : 1 }}
                          disabled={loading}
                        >
                          {u.isActive ? "Sperren" : "Entsperren"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-4" style={{ color: theme.gray }}>
                      Keine Mitglieder
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function IdeaComposer({ onCreate }: { onCreate: (data: { title: string; summary: string; tags: string[] }) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [tags, setTags] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!title.trim() || !summary.trim()) {
      setError("Bitte Titel und Beschreibung angeben");
      return;
    }
    setSubmitting(true);
    try {
      const normalizedTags = tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      await onCreate({ title: title.trim(), summary: summary.trim(), tags: normalizedTags });
      setTitle("");
      setSummary("");
      setTags("");
      setOpen(false);
    } catch (err) {
      setError((err as Error).message || "Speichern fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <button
        onClick={() => setOpen(true)}
        className="rounded-xl px-5 py-3 font-medium"
        style={{ background: theme.gold, color: "#0C1117" }}
      >
        + Idee einreichen
      </button>
      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="w-full max-w-2xl rounded-2xl p-6" style={{ background: theme.card, border: `1px solid ${theme.cardBorder}` }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Neue Idee</h3>
              <button onClick={() => setOpen(false)} className="px-3 py-1 rounded-lg" style={{ border: `1px solid ${theme.cardBorder}` }}>
                Schließen
              </button>
            </div>
            <form onSubmit={handleSubmit} className="grid gap-4">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Titel"
                className="rounded-xl px-4 py-3 outline-none"
                style={{ background: theme.input, border: `1px solid ${theme.cardBorder}`, color: theme.text }}
              />
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={5}
                placeholder="Kurzbeschreibung"
                className="rounded-xl px-4 py-3 outline-none"
                style={{ background: theme.input, border: `1px solid ${theme.cardBorder}`, color: theme.text }}
              />
              <input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="Tags (kommagetrennt)"
                className="rounded-xl px-4 py-3 outline-none"
                style={{ background: theme.input, border: `1px solid ${theme.cardBorder}`, color: theme.text }}
              />
              {error && (
                <div className="text-sm" style={{ color: "#FCA5A5" }}>
                  {error}
                </div>
              )}
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  className="rounded-xl px-4 py-2"
                  style={{ background: theme.gold, color: "#0C1117", opacity: submitting ? 0.7 : 1 }}
                  disabled={submitting}
                >
                  Speichern
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded-xl px-4 py-2"
                  style={{ border: `1px solid ${theme.cardBorder}` }}
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function IdeaCard({ idea, onVote, onOpenComments }: { idea: Idea; onVote: (id: string) => void; onOpenComments: (idea: Idea) => void }) {
  return (
    <div className="rounded-2xl p-5 flex flex-col" style={{ background: theme.card, border: `1px solid ${theme.cardBorder}` }}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">{idea.title}</h3>
          <div className="text-xs" style={{ color: theme.gray }}>
            {new Date(idea.createdAt).toLocaleString()}
          </div>
        </div>
        <button
          onClick={() => onVote(idea.id)}
          className="rounded-lg px-3 py-1 text-sm font-semibold"
          style={{ background: theme.gold, color: "#0C1117" }}
        >
          ▲ {idea.voteCount}
        </button>
      </div>
      {idea.tags?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {idea.tags.map((t) => (
            <span
              key={t}
              className="text-xs px-2 py-1 rounded-full"
              style={{ background: theme.input, border: `1px solid ${theme.cardBorder}`, color: theme.gray }}
            >
              #{t}
            </span>
          ))}
        </div>
      )}
      <p className="mt-3 text-sm" style={{ color: theme.gray }}>
        {idea.summary}
      </p>
      <div className="mt-auto pt-4 flex items-center justify-between text-xs" style={{ color: theme.gray }}>
        <span>von {idea.author}</span>
        <button onClick={() => onOpenComments(idea)} className="underline">
          Kommentare ({idea.commentCount})
        </button>
      </div>
    </div>
  );
}

function IdeaGrid({ ideas, onVote, onOpenComments }: { ideas: Idea[]; onVote: (id: string) => void; onOpenComments: (idea: Idea) => void }) {
  if (!ideas.length) {
    return (
      <div className="rounded-2xl p-8 text-center" style={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, color: theme.gray }}>
        Noch keine Ideen - sei die/der Erste!
      </div>
    );
  }
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {ideas.map((idea) => (
        <IdeaCard key={idea.id} idea={idea} onVote={onVote} onOpenComments={onOpenComments} />
      ))}
    </div>
  );
}

function CommentsModal({
  idea,
  state,
  onClose,
  onAdd,
}: {
  idea: Idea;
  state: CommentState;
  onClose: () => void;
  onAdd: (text: string) => Promise<void>;
}) {
  const [text, setText] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [state.items]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await onAdd(text.trim());
      setText("");
    } catch (err) {
      setSubmitError((err as Error).message || "Kommentar konnte nicht gespeichert werden");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center" style={{ background: "rgba(0,0,0,0.5)" }}>
      <div className="w-full max-w-2xl rounded-2xl p-6" style={{ background: theme.card, border: `1px solid ${theme.cardBorder}` }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Kommentare - {idea.title}</h3>
          <button onClick={onClose} className="px-3 py-1 rounded-lg" style={{ border: `1px solid ${theme.cardBorder}` }}>
            Schließen
          </button>
        </div>
        <div ref={listRef} className="max-h-64 overflow-y-auto pr-2 space-y-4">
          {state.loading ? (
            <p className="text-sm" style={{ color: theme.gray }}>
              Lädt...
            </p>
          ) : state.items.length ? (
            state.items.map((c) => (
              <div key={c.id} className="rounded-xl p-3" style={{ background: theme.input, border: `1px solid ${theme.cardBorder}` }}>
                <div className="text-xs mb-1" style={{ color: theme.gray }}>
                  {c.author} • {new Date(c.createdAt).toLocaleString()}
                </div>
                <div className="text-sm">{c.body}</div>
              </div>
            ))
          ) : (
            <p className="text-sm" style={{ color: theme.gray }}>
              Noch keine Kommentare.
            </p>
          )}
          {state.error && (
            <p className="text-sm" style={{ color: "#FCA5A5" }}>
              {state.error}
            </p>
          )}
        </div>
        <form onSubmit={handleSubmit} className="mt-4 flex gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Kommentar hinzufügen"
            className="flex-1 rounded-xl px-4 py-3 outline-none"
            style={{ background: theme.input, border: `1px solid ${theme.cardBorder}`, color: theme.text }}
          />
          <button
            type="submit"
            className="rounded-xl px-4 py-2 font-medium"
            style={{ background: theme.gold, color: "#0C1117", opacity: submitting ? 0.7 : 1 }}
            disabled={submitting}
          >
            Posten
          </button>
        </form>
        {submitError && (
          <p className="mt-3 text-sm" style={{ color: "#FCA5A5" }}>
            {submitError}
          </p>
        )}
      </div>
    </div>
  );
}

function Footer() {
  return (
    <footer className="mt-10 pt-6 border-t" style={{ borderColor: theme.cardBorder, color: theme.gray }}>
      <div className="text-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <span>© {new Date().getFullYear()} The AI Capital Society</span>
        <span>Kontakt: contact@aicapital.society (Platzhalter)</span>
      </div>
    </footer>
  );
}

function UserBadge({ user, onLogout }: { user: AuthUser; onLogout: () => void }) {
  return (
    <div className="flex items-center gap-3 rounded-xl px-3 py-2" style={{ border: `1px solid ${theme.cardBorder}`, background: theme.input }}>
      <LogoMark size={24} />
      <div>
        <div className="text-xs" style={{ color: theme.gray }}>
          Angemeldet als
        </div>
        <div className="text-sm font-medium">
          {user.username} {user.isAdmin ? <span className="text-xs" style={{ color: theme.gray }}>(Admin)</span> : null}
        </div>
      </div>
      <button onClick={onLogout} className="ml-2 text-xs underline" style={{ color: theme.gray }}>
        Abmelden
      </button>
    </div>
  );
}

function SortBar({ sortBy, setSortBy, search, setSearch }: { sortBy: string; setSortBy: (value: string) => void; search: string; setSearch: (value: string) => void }) {
  const sortOptions: [string, string][] = [
    ["top", "Meiste Votes"],
    ["new", "Neueste"],
    ["hot", "Heiß (Votes & Zeit)"],
  ];
  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 py-4">
      <div className="flex items-center gap-2 text-sm">
        <span style={{ color: theme.gray }}>Sortieren nach:</span>
        {sortOptions.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setSortBy(key)}
            className={`px-3 py-1 rounded-full ${sortBy === key ? "font-semibold" : ""}`}
            style={{ border: `1px solid ${theme.cardBorder}`, background: sortBy === key ? theme.input : "transparent", color: theme.text }}
          >
            {label}
          </button>
        ))}
      </div>
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Suchen nach Titel oder Tag..."
        className="w-full sm:w-80 rounded-xl px-4 py-3 outline-none"
        style={{ background: theme.input, border: `1px solid ${theme.cardBorder}`, color: theme.text }}
      />
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loadingIdeas, setLoadingIdeas] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [sortBy, setSortBy] = useState("top");
  const [search, setSearch] = useState("");
  const [globalError, setGlobalError] = useState("");

  const [activeIdea, setActiveIdea] = useState<Idea | null>(null);
  const [comments, setComments] = useState<Record<string, CommentState>>({});

  const [invites, setInvites] = useState<Invite[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [adminLoading, setAdminLoading] = useState(false);

  useEffect(() => {
    async function bootstrap() {
      const existing = getToken();
      if (!existing) {
        setInitialized(true);
        return;
      }
      try {
        const me = await api.me();
        setUser(me.user);
        await loadIdeas();
      } catch (err) {
        console.error(err);
        setToken(null);
      } finally {
        setInitialized(true);
      }
    }
    bootstrap();
  }, []);

  useEffect(() => {
    if (!user?.isAdmin) {
      setInvites([]);
      setAdminUsers([]);
      return;
    }
    refreshAdminData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.isAdmin]);

  const refreshAdminData = async () => {
    if (!user?.isAdmin) return;
    setAdminLoading(true);
    try {
      const [inviteList, userList] = await Promise.all([api.listInvites(), api.listUsers()]);
      setInvites(inviteList);
      setAdminUsers(userList);
    } catch (err) {
      setGlobalError((err as Error).message || "Admin-Daten konnten nicht geladen werden");
    } finally {
      setAdminLoading(false);
    }
  };

  const loadIdeas = async () => {
    setLoadingIdeas(true);
    setGlobalError("");
    try {
      const list = await api.listIdeas();
      setIdeas(list);
    } catch (err) {
      setGlobalError((err as Error).message || "Ideen konnten nicht geladen werden");
    } finally {
      setLoadingIdeas(false);
    }
  };

  const handleAuthSuccess = async (nextUser: AuthUser) => {
    setUser(nextUser);
    await loadIdeas();
    if (nextUser.isAdmin) {
      await refreshAdminData();
    }
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    setIdeas([]);
    setInvites([]);
    setAdminUsers([]);
  };

  const sortedIdeas = useMemo(() => {
    const q = search.toLowerCase();
    const now = Date.now();
    const filtered = ideas.filter((idea) => {
      const matchesTitle = idea.title.toLowerCase().includes(q);
      const matchesTags = (idea.tags || []).join(",").toLowerCase().includes(q);
      return !q || matchesTitle || matchesTags;
    });

    const clone = [...filtered];
    if (sortBy === "top") {
      clone.sort((a, b) => b.voteCount - a.voteCount);
    } else if (sortBy === "new") {
      clone.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    } else if (sortBy === "hot") {
      const hot = (idea: Idea) => {
        const ageHours = (now - new Date(idea.createdAt).getTime()) / 3600000 + 2;
        return idea.voteCount / Math.pow(ageHours, 1.2);
      };
      clone.sort((a, b) => hot(b) - hot(a));
    }
    return clone;
  }, [ideas, sortBy, search]);

  const handleCreateIdea = async (payload: { title: string; summary: string; tags: string[] }) => {
    const created = await api.createIdea(payload);
    setIdeas((prev) => [created, ...prev]);
  };

  const handleVote = async (id: string) => {
    try {
      const res = await api.voteIdea(id);
      setIdeas((prev) => prev.map((idea) => (idea.id === id ? { ...idea, voteCount: res.votes } : idea)));
    } catch (err) {
      setGlobalError((err as Error).message || "Vote fehlgeschlagen");
    }
  };

  const openComments = (idea: Idea) => {
    setActiveIdea(idea);
    const existing = comments[idea.id];
    if (!existing) {
      setComments((prev) => ({ ...prev, [idea.id]: { items: [], loading: true, error: null } }));
      void fetchComments(idea.id);
    } else if (existing.error) {
      setComments((prev) => ({ ...prev, [idea.id]: { ...existing, loading: true, error: null } }));
      void fetchComments(idea.id);
    }
  };

  const fetchComments = async (ideaId: string) => {
    setComments((prev) => ({
      ...prev,
      [ideaId]: { items: prev[ideaId]?.items ?? [], loading: true, error: null },
    }));
    try {
      const list = await api.listComments(ideaId);
      setComments((prev) => ({ ...prev, [ideaId]: { items: list, loading: false, error: null } }));
      setIdeas((prev) => prev.map((idea) => (idea.id === ideaId ? { ...idea, commentCount: list.length } : idea)));
    } catch (err) {
      setComments((prev) => ({
        ...prev,
        [ideaId]: { items: prev[ideaId]?.items ?? [], loading: false, error: (err as Error).message || "Kommentare konnten nicht geladen werden" },
      }));
    }
  };

  const handleAddComment = async (ideaId: string, text: string) => {
    const result = await api.addComment(ideaId, text);
    setComments((prev) => {
      const existing = prev[ideaId]?.items ?? [];
      const items = [...existing, result];
      return { ...prev, [ideaId]: { items, loading: false, error: null } };
    });
    setIdeas((prev) => prev.map((idea) => (idea.id === ideaId ? { ...idea, commentCount: idea.commentCount + 1 } : idea)));
  };

  const currentComments = activeIdea ? comments[activeIdea.id] : undefined;

  if (!initialized) {
    return (
      <Shell>
        <BrandHeader />
        <div className="rounded-2xl p-8 text-center" style={{ background: theme.card, border: `1px solid ${theme.cardBorder}` }}>
          Lädt...
        </div>
      </Shell>
    );
  }

  if (!user) {
    return (
      <Shell>
        <BrandHeader />
        <Gate onSuccess={handleAuthSuccess} />
        <Footer />
      </Shell>
    );
  }

  return (
    <Shell>
      <BrandHeader />

      {globalError && (
        <div className="mb-6 rounded-xl p-4" style={{ background: "#451a1a", border: "1px solid #7f1d1d", color: "#fecaca" }}>
          {globalError}
        </div>
      )}

      <AdminPanel
        currentUser={user}
        invites={invites}
        users={adminUsers}
        onCreateInvite={async () => {
          try {
            await api.createInvite();
            await refreshAdminData();
          } catch (err) {
            setGlobalError((err as Error).message || "Einladung konnte nicht erstellt werden");
          }
        }}
        onDeleteInvite={async (code) => {
          try {
            await api.deleteInvite(code);
            await refreshAdminData();
          } catch (err) {
            setGlobalError((err as Error).message || "Einladung konnte nicht entfernt werden");
          }
        }}
        onToggleActive={async (id) => {
          try {
            await api.toggleUserActive(id);
            await refreshAdminData();
          } catch (err) {
            setGlobalError((err as Error).message || "Aktualisierung fehlgeschlagen");
          }
        }}
        onToggleAdmin={async (id) => {
          try {
            await api.toggleUserAdmin(id);
            if (id === user.id) {
              const me = await api.me();
              setUser(me.user);
            }
            await refreshAdminData();
          } catch (err) {
            setGlobalError((err as Error).message || "Aktualisierung fehlgeschlagen");
          }
        }}
        loading={adminLoading}
      />

      <div className="rounded-2xl p-6 mb-6" style={{ background: theme.card, border: `1px solid ${theme.cardBorder}` }}>
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
          <div>
            <h2 className="text-xl font-semibold">Ideenboard</h2>
            <p className="text-sm" style={{ color: theme.gray }}>
              Sammle, bewerte und diskutiere Investierbares rund um KI.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <IdeaComposer onCreate={handleCreateIdea} />
            <UserBadge user={user} onLogout={handleLogout} />
          </div>
        </div>
        <SortBar sortBy={sortBy} setSortBy={setSortBy} search={search} setSearch={setSearch} />
      </div>

      {loadingIdeas ? (
        <div className="rounded-2xl p-8 text-center" style={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, color: theme.gray }}>
          Ideen werden geladen...
        </div>
      ) : (
        <IdeaGrid ideas={sortedIdeas} onVote={handleVote} onOpenComments={openComments} />
      )}

      {activeIdea && currentComments && (
        <CommentsModal
          idea={activeIdea}
          state={currentComments}
          onClose={() => setActiveIdea(null)}
          onAdd={async (text) => handleAddComment(activeIdea.id, text)}
        />
      )}

      <Footer />
    </Shell>
  );
}
